import { useLazyQuery, useQuery } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Queries } from '@shared/api';
import { API } from '@shared/types/api';

interface UseListUsersInput {
  input: API.ListUsers;
  customCacheKey?: string;
  skip?: boolean;
}

interface UseListUsersResult {
  users: NonNullable<NonNullable<API.ListUsersQuery['listUsers']>['users']>[number][];
  total: number;
  loading: boolean;
  error?: Error;
  hasMore: boolean;
  loadMore: () => void;
}

interface UseListUsersLazyResult {
  users: NonNullable<NonNullable<API.ListUsersQuery['listUsers']>['users']>[number][];
  totalCount: number;
  loading: boolean;
  error?: Error;
  hasMore: boolean;
  loadMore: () => void;
  fetch: () => void;
}

const inputDefaults: API.ListUsers = {
  excludeSystemUsers: true,
  orderBys: [{ orderBy: 'createdAt' as const, direction: 'DESC' as const }],
};

export function useListUsers({
  input,
  customCacheKey,
  skip,
}: UseListUsersInput): UseListUsersResult {
  const queryInput = { ...inputDefaults, ...input };
  const variables = {
    input: queryInput,
    customCacheKey: customCacheKey ?? '',
  };
  const { data, loading, error, fetchMore } = useQuery<API.ListUsersQuery>(Queries.user.listUsers, {
    variables,
    skip,
  });

  const {
    ids,
    name,
    limit,
    excludeSystemUsers,
    isActive,
    isDeactivated,
    orderBys,
    memberOfChannelIds,
    notMemberOfChannelIds,
  } = queryInput;
  const loadMore = useCallback(() => {
    if (!isNil(data?.listUsers?.users) && data?.listUsers?.hasMore) {
      const offset = data.listUsers.users.length;
      fetchMore({
        variables: {
          input: {
            ids,
            name,
            limit,
            offset,
            excludeSystemUsers,
            isActive,
            isDeactivated,
            orderBys,
            memberOfChannelIds,
            notMemberOfChannelIds,
          },
          customCacheKey: customCacheKey ?? '',
        },
      });
    }
  }, [
    data?.listUsers,
    fetchMore,
    ids,
    limit,
    name,
    customCacheKey,
    excludeSystemUsers,
    isActive,
    isDeactivated,
    orderBys,
    memberOfChannelIds,
    notMemberOfChannelIds,
  ]);

  return useMemo(
    () => ({
      users: data?.listUsers?.users ?? [],
      total: data?.listUsers?.total ?? 0,
      hasMore: data?.listUsers?.hasMore ?? false,
      loadMore,
      loading,
      error,
    }),
    [data?.listUsers, loadMore, loading, error],
  );
}

export function useListUsersById(
  ids: number[],
  orderBys?: API.ListUsers['orderBys'],
): UseListUsersResult {
  const input = {
    ids,
    includeDeactivated: true,
    excludeSystemUsers: true,
    orderBys: orderBys ?? [{ orderBy: 'createdAt' as const, direction: 'DESC' as const }],
  };
  const skip = isEmpty(ids);
  return useListUsers({ input, skip });
}

export function useListUsersByName(
  name: string,
  orderBys?: API.ListUsers['orderBys'],
): UseListUsersResult {
  return useListUsers({
    input: {
      name: name.trim(),
      orderBys: orderBys ?? [{ orderBy: 'createdAt' as const, direction: 'DESC' as const }],
    },
  });
}

export function useUsersWithoutMe<T extends API.ShortUserFragment>(users: T[]): T[] {
  return useMemo(() => getUsersWithoutMe(users), [users]);
}

export function getUsersWithoutMe<T extends API.ShortUserFragment>(users: T[]): T[] {
  return users.filter(user => !user.isMe);
}

export function useSortedUsersWithoutMe<T extends API.ShortUserFragment>(users: T[]): T[] {
  return useMemo(() => getSortedUsersWithoutMe(users), [users]);
}

export function getSortedUsersWithoutMe<T extends API.ShortUserFragment>(users: T[]): T[] {
  return getUsersWithoutMe(users).sort((a, b) => a.fullName.localeCompare(b.fullName));
}

export function useListUsersLazy({
  input,
  customCacheKey,
}: Omit<UseListUsersInput, 'skip'>): UseListUsersLazyResult {
  const queryInput = { ...inputDefaults, ...input };
  const [fetch, { data, loading, error, fetchMore }] = useLazyQuery<API.ListUsersQuery>(
    Queries.user.listUsers,
    {
      variables: {
        input: queryInput,
        customCacheKey: customCacheKey ?? '',
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const {
    ids,
    name,
    limit,
    excludeSystemUsers,
    isActive,
    isDeactivated,
    orderBys,
    memberOfChannelIds,
    notMemberOfChannelIds,
  } = queryInput;
  const loadMore = useCallback(() => {
    if (!isNil(data?.listUsers?.users) && data?.listUsers?.hasMore) {
      const offset = data.listUsers.users.length;
      fetchMore({
        variables: {
          input: {
            ids,
            name,
            limit,
            offset,
            excludeSystemUsers,
            isActive,
            isDeactivated,
            orderBys,
            memberOfChannelIds,
            notMemberOfChannelIds,
          },
          customCacheKey: customCacheKey ?? '',
        },
      });
    }
  }, [
    data?.listUsers,
    fetchMore,
    ids,
    limit,
    name,
    customCacheKey,
    excludeSystemUsers,
    isActive,
    isDeactivated,
    orderBys,
    memberOfChannelIds,
    notMemberOfChannelIds,
  ]);

  return useMemo(
    () => ({
      users: data?.listUsers?.users ?? [],
      hasMore: data?.listUsers?.hasMore ?? false,
      totalCount: data?.listUsers?.total ?? 0,
      loadMore,
      loading,
      error,
      fetch,
    }),
    [
      data?.listUsers?.users,
      loadMore,
      loading,
      error,
      data?.listUsers?.hasMore,
      fetch,
      data?.listUsers?.total,
    ],
  );
}
